import React, { useCallback, useEffect, useMemo, useRef } from "react";
import cloneDeep from "lodash/cloneDeep";
import { IfPermitted, MultiSelect, SourceCodeEditor, TimezoneSelect } from "components/common";
import { LookupTableFields } from "components/lookup-tables";
import UsersSelectField from "components/users/UsersSelectField";
import { ControlLabel, FormGroup, HelpBlock, Input } from "components/bootstrap";
import { getValueFromInput } from "util/FormsUtils";
import HideOnCloud from "util/conditional/HideOnCloud";
import usePluggableLicenseCheck from "hooks/usePluggableLicenseCheck";
import usePluginEntities from "hooks/usePluginEntities";
import {
  DEFAULT_BODY_TEMPLATE,
  DEFAULT_HTML_BODY_TEMPLATE
} from "components/event-notifications/event-notification-types/emailNotificationTemplates";
const LOOKUP_KEY_PLACEHOLDER_TEXT = "${event.group_by_fields.group_by_field}";
const EventProcedureCheckbox = ({ checked, onChange }) => {
  const {
    data: { valid: validSecurityLicense }
  } = usePluggableLicenseCheck("/license/security");
  if (!validSecurityLicense) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      type: "checkbox",
      id: "include_event_procedure",
      name: "include_event_procedure",
      label: "Include Event Procedure in Email Body",
      onChange,
      checked
    }
  ));
};
const EmailTemplatesRunner = ({
  config,
  onChange,
  resetKey = void 0
}) => {
  var _a, _b, _c, _d;
  const { data: { valid: validCustomizationLicense } = { valid: false } } = usePluggableLicenseCheck(
    "/license/enterprise/customization"
  );
  const entities = usePluginEntities("customization.emailTemplates");
  const providingEntity = useMemo(
    () => (entities != null ? entities : []).find((e) => {
      var _a2;
      return typeof ((_a2 = e == null ? void 0 : e.hooks) == null ? void 0 : _a2.useEmailTemplate) === "function";
    }),
    [entities]
  );
  const noopUseEmailTemplate = useCallback(() => ({ templateConfig: void 0 }), []);
  const useEmailTemplateHook = (_b = (_a = providingEntity == null ? void 0 : providingEntity.hooks) == null ? void 0 : _a.useEmailTemplate) != null ? _b : noopUseEmailTemplate;
  const { templateConfig } = useEmailTemplateHook() || {};
  const key = String(resetKey != null ? resetKey : "default");
  const sig = validCustomizationLicense && templateConfig ? `${key}|${templateConfig.override_defaults ? "1" : "0"}|${(_c = templateConfig.text_body) != null ? _c : ""}|${(_d = templateConfig.html_body) != null ? _d : ""}` : `${key}|no-license-or-config`;
  const lastSigRef = useRef("init");
  useEffect(() => {
    var _a2, _b2, _c2, _d2, _e, _f, _g, _h;
    if (!validCustomizationLicense || !templateConfig) return;
    if (lastSigRef.current === sig) return;
    const { override_defaults, text_body, html_body } = templateConfig;
    let next = config;
    let changed = false;
    if (override_defaults === true) {
      const nextCfg = { ...config };
      const trimmedBody = ((_a2 = config.body_template) != null ? _a2 : "").trim();
      const trimmedHtml = ((_b2 = config.html_body_template) != null ? _b2 : "").trim();
      const shouldOverrideBody = typeof text_body === "string" && (trimmedBody === "" || ((_c2 = config.body_template) != null ? _c2 : "") === DEFAULT_BODY_TEMPLATE) && text_body !== config.body_template;
      const shouldOverrideHtml = typeof html_body === "string" && (trimmedHtml === "" || ((_d2 = config.html_body_template) != null ? _d2 : "") === DEFAULT_HTML_BODY_TEMPLATE) && html_body !== config.html_body_template;
      if (shouldOverrideBody) {
        nextCfg.body_template = text_body;
        changed = true;
      }
      if (shouldOverrideHtml) {
        nextCfg.html_body_template = html_body;
        changed = true;
      }
      if (changed) next = nextCfg;
    } else {
      const trimmedBody = ((_e = config.body_template) != null ? _e : "").trim();
      const trimmedHtml = ((_f = config.html_body_template) != null ? _f : "").trim();
      const hasCustomBody = trimmedBody !== "" && ((_g = config.body_template) != null ? _g : "") !== DEFAULT_BODY_TEMPLATE && trimmedBody !== DEFAULT_BODY_TEMPLATE;
      const hasCustomHtml = trimmedHtml !== "" && ((_h = config.html_body_template) != null ? _h : "") !== DEFAULT_HTML_BODY_TEMPLATE && trimmedHtml !== DEFAULT_HTML_BODY_TEMPLATE;
      if (hasCustomBody || hasCustomHtml) {
        lastSigRef.current = sig;
        return;
      }
      const nextCfg = { ...config };
      if (trimmedBody === "") {
        nextCfg.body_template = DEFAULT_BODY_TEMPLATE;
        changed = true;
      }
      if (trimmedHtml === "") {
        nextCfg.html_body_template = DEFAULT_HTML_BODY_TEMPLATE;
        changed = true;
      }
      if (changed) next = nextCfg;
    }
    if (changed) onChange(next);
    lastSigRef.current = sig;
  }, [sig, validCustomizationLicense, templateConfig, config, onChange]);
  return null;
};
class EmailNotificationForm extends React.Component {
  constructor() {
    super(...arguments);
    this.propagateChange = (key, value) => {
      const { config, onChange } = this.props;
      const nextConfig = cloneDeep(config);
      nextConfig[key] = value;
      onChange(nextConfig);
    };
    this.handleChange = (event) => {
      const { name } = event.target;
      this.propagateChange(name, getValueFromInput(event.target));
    };
    this.handleSelectChange = (key, value) => {
      this.propagateChange(key, value);
    };
    this.handleTimeZoneChange = (nextValue) => {
      this.propagateChange("time_zone", nextValue);
    };
    this.handleUseRecipientLookupChange = (event) => {
      const { config, onChange } = this.props;
      const nextConfig = cloneDeep(config);
      const nextValue = getValueFromInput(event.target);
      nextConfig.lookup_recipient_emails = nextValue;
      if (nextValue) {
        nextConfig.email_recipients = [];
      } else {
        nextConfig.recipients_lut_name = null;
        nextConfig.recipients_lut_key = null;
      }
      onChange(nextConfig);
    };
    this.handleUseCcLookupChange = (event) => {
      const { config, onChange } = this.props;
      const nextConfig = cloneDeep(config);
      const nextValue = getValueFromInput(event.target);
      nextConfig.lookup_cc_emails = nextValue;
      if (nextValue) {
        nextConfig.cc_emails = [];
      } else {
        nextConfig.cc_emails_lut_name = null;
        nextConfig.cc_emails_lut_key = null;
      }
      onChange(nextConfig);
    };
    this.handleUseBccLookupChange = (event) => {
      const { config, onChange } = this.props;
      const nextConfig = cloneDeep(config);
      const nextValue = getValueFromInput(event.target);
      nextConfig.lookup_bcc_emails = nextValue;
      if (nextValue) {
        nextConfig.bcc_emails = [];
      } else {
        nextConfig.bcc_emails_lut_name = null;
        nextConfig.bcc_emails_lut_key = null;
      }
      onChange(nextConfig);
    };
    this.handleUseReplyToLookupChange = (event) => {
      const { config, onChange } = this.props;
      const nextConfig = cloneDeep(config);
      const nextValue = getValueFromInput(event.target);
      nextConfig.lookup_reply_to_email = nextValue;
      if (nextValue) {
        nextConfig.reply_to = "";
      } else {
        nextConfig.reply_to_lut_name = null;
        nextConfig.reply_to_lut_key = null;
      }
      onChange(nextConfig);
    };
    this.handleUseSenderLookupChange = (event) => {
      const { config, onChange } = this.props;
      const nextConfig = cloneDeep(config);
      const nextValue = getValueFromInput(event.target);
      nextConfig.lookup_sender_email = nextValue;
      if (nextValue) {
        nextConfig.sender = "";
      } else {
        nextConfig.sender_lut_name = null;
        nextConfig.sender_lut_key = null;
      }
      onChange(nextConfig);
    };
    this.handleBodyTemplateChange = (nextValue) => {
      this.propagateChange("body_template", nextValue);
    };
    this.handleHtmlBodyTemplateChange = (nextValue) => {
      this.propagateChange("html_body_template", nextValue);
    };
    this.handleRecipientsChange = (key) => (nextValue) => this.propagateChange(key, nextValue === "" ? [] : nextValue.split(","));
    this.emailRecipientsFormGroup = () => {
      var _a, _b;
      const { config, validation } = this.props;
      return /* @__PURE__ */ React.createElement(
        FormGroup,
        {
          controlId: "notification-email-recipients",
          validationState: validation.errors.recipients ? "error" : null
        },
        /* @__PURE__ */ React.createElement(ControlLabel, null, "Email recipient(s) ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, "(Optional)")),
        /* @__PURE__ */ React.createElement(
          MultiSelect,
          {
            id: "notification-email-recipients",
            value: Array.isArray(config.email_recipients) ? config.email_recipients.join(",") : "",
            addLabelText: 'Add email "{label}"?',
            placeholder: "Type email address",
            options: [],
            onChange: this.handleRecipientsChange("email_recipients"),
            allowCreate: true
          }
        ),
        /* @__PURE__ */ React.createElement(HelpBlock, null, ((_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.recipients) == null ? void 0 : _b[0]) || "Add email addresses that will receive this Notification.")
      );
    };
    this.emailLookupFormGroup = () => {
      var _a, _b;
      const { config, validation } = this.props;
      const customKeyField = /* @__PURE__ */ React.createElement(
        Input,
        {
          id: "recipients-table-key",
          name: "recipients_lut_key",
          label: "Recipients Lookup Table Key",
          type: "text",
          placeholder: LOOKUP_KEY_PLACEHOLDER_TEXT,
          bsStyle: validation.errors.recipients_lut_key ? "error" : null,
          help: ((_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.recipients_lut_key) == null ? void 0 : _b[0]) || "Event Field name whose value will be used as Lookup Table Key.",
          value: config.recipients_lut_key || "",
          onChange: this.handleChange,
          required: true
        }
      );
      return /* @__PURE__ */ React.createElement(
        LookupTableFields,
        {
          onTableNameChange: (value) => this.handleSelectChange("recipients_lut_name", value),
          onKeyChange: (value) => this.handleSelectChange("recipients_lut_key", value),
          selectedTableName: config.recipients_lut_name || "",
          selectedKeyName: config.recipients_lut_key || "",
          nameValidation: validation.errors.recipients_lut_name,
          keyValidation: validation.errors.recipients_lut_key,
          lookupTableNameLabel: "Recipients Lookup Table Name",
          customKeyField
        }
      );
    };
    this.ccEmailsFormGroup = () => {
      var _a, _b;
      const { config, validation } = this.props;
      return /* @__PURE__ */ React.createElement(FormGroup, { controlId: "notification-cc-emails", validationState: validation.errors.cc_emails ? "error" : null }, /* @__PURE__ */ React.createElement(ControlLabel, null, "CC Email(s) ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, "(Optional)")), /* @__PURE__ */ React.createElement(
        MultiSelect,
        {
          id: "notification-cc-email",
          value: Array.isArray(config.cc_emails) ? config.cc_emails.join(",") : "",
          addLabelText: 'Add email "{label}"?',
          placeholder: "Type email address",
          options: [],
          onChange: this.handleRecipientsChange("cc_emails"),
          allowCreate: true
        }
      ), /* @__PURE__ */ React.createElement(HelpBlock, null, ((_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.cc_emails) == null ? void 0 : _b[0]) || "Add email addresses that will be on the CC line of this Notification."));
    };
    this.ccLookupFormGroup = () => {
      var _a, _b;
      const { config, validation } = this.props;
      const customKeyField = /* @__PURE__ */ React.createElement(
        Input,
        {
          id: "cc-emails-table-key",
          name: "cc_emails_lut_key",
          label: "CC Emails Lookup Table Key",
          type: "text",
          placeholder: LOOKUP_KEY_PLACEHOLDER_TEXT,
          bsStyle: validation.errors.cc_emails_lut_key ? "error" : null,
          help: ((_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.cc_emails_lut_key) == null ? void 0 : _b[0]) || "Event Field name whose value will be used as Lookup Table Key.",
          value: config.cc_emails_lut_key || "",
          onChange: this.handleChange,
          required: true
        }
      );
      return /* @__PURE__ */ React.createElement(
        LookupTableFields,
        {
          onTableNameChange: (value) => this.handleSelectChange("cc_emails_lut_name", value),
          onKeyChange: (value) => this.handleSelectChange("cc_emails_lut_key", value),
          selectedTableName: config.cc_emails_lut_name || "",
          selectedKeyName: config.cc_emails_lut_key || "",
          nameValidation: validation.errors.cc_emails_lut_name,
          keyValidation: validation.errors.cc_emails_lut_key,
          lookupTableNameLabel: "CC Emails Lookup Table Name",
          customKeyField
        }
      );
    };
    this.bccEmailsFormGroup = () => {
      var _a, _b;
      const { config, validation } = this.props;
      return /* @__PURE__ */ React.createElement(FormGroup, { controlId: "notification-bcc-emails", validationState: validation.errors.bcc_emails ? "error" : null }, /* @__PURE__ */ React.createElement(ControlLabel, null, "BCC Email(s) ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, "(Optional)")), /* @__PURE__ */ React.createElement(
        MultiSelect,
        {
          id: "notification-bcc-email",
          value: Array.isArray(config.bcc_emails) ? config.bcc_emails.join(",") : "",
          addLabelText: 'Add email "{label}"?',
          placeholder: "Type email address",
          options: [],
          onChange: this.handleRecipientsChange("bcc_emails"),
          allowCreate: true
        }
      ), /* @__PURE__ */ React.createElement(HelpBlock, null, ((_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.bcc_emails) == null ? void 0 : _b[0]) || "Add email addresses that will be on the BCC line of this Notification."));
    };
    this.bccLookupFormGroup = () => {
      var _a, _b;
      const { config, validation } = this.props;
      const customKeyField = /* @__PURE__ */ React.createElement(
        Input,
        {
          id: "bcc-emails-table-key",
          name: "bcc_emails_lut_key",
          label: "BCC Emails Lookup Table Key",
          type: "text",
          placeholder: LOOKUP_KEY_PLACEHOLDER_TEXT,
          bsStyle: validation.errors.bcc_emails_lut_key ? "error" : null,
          help: ((_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.bcc_emails_lut_key) == null ? void 0 : _b[0]) || "Event Field name whose value will be used as Lookup Table Key.",
          value: config.bcc_emails_lut_key || "",
          onChange: this.handleChange,
          required: true
        }
      );
      return /* @__PURE__ */ React.createElement(
        LookupTableFields,
        {
          onTableNameChange: (value) => this.handleSelectChange("bcc_emails_lut_name", value),
          onKeyChange: (value) => this.handleSelectChange("bcc_emails_lut_key", value),
          selectedTableName: config.bcc_emails_lut_name || "",
          selectedKeyName: config.bcc_emails_lut_key || "",
          nameValidation: validation.errors.bcc_emails_lut_name,
          keyValidation: validation.errors.bcc_emails_lut_key,
          lookupTableNameLabel: "BCC Emails Lookup Table Name",
          customKeyField
        }
      );
    };
    this.senderInput = () => {
      var _a, _b;
      const { config, validation } = this.props;
      return /* @__PURE__ */ React.createElement(
        Input,
        {
          id: "notification-sender",
          name: "sender",
          label: /* @__PURE__ */ React.createElement(ControlLabel, null, "Sender ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, "(Optional)")),
          type: "text",
          bsStyle: validation.errors.sender ? "error" : null,
          help: ((_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.sender) == null ? void 0 : _b[0]) || "The email address that should be used as the notification sender. Leave it empty to use the default sender address.",
          value: config.sender || "",
          onChange: this.handleChange
        }
      );
    };
    this.senderLookupFormGroup = () => {
      var _a, _b;
      const { config, validation } = this.props;
      const customKeyField = /* @__PURE__ */ React.createElement(
        Input,
        {
          id: "sender-lookup-table-key",
          name: "sender_lut_key",
          label: "Sender Lookup Table Key",
          type: "text",
          placeholder: LOOKUP_KEY_PLACEHOLDER_TEXT,
          bsStyle: validation.errors.sender_lut_key ? "error" : null,
          help: ((_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.sender_lut_key) == null ? void 0 : _b[0]) || "Event Field name whose value will be used as Lookup Table Key.",
          value: config.sender_lut_key || "",
          onChange: this.handleChange,
          required: true
        }
      );
      return /* @__PURE__ */ React.createElement(
        LookupTableFields,
        {
          onTableNameChange: (value) => this.handleSelectChange("sender_lut_name", value),
          onKeyChange: (value) => this.handleSelectChange("sender_lut_key", value),
          selectedTableName: config.sender_lut_name || "",
          selectedKeyName: config.sender_lut_key || "",
          nameValidation: validation.errors.sender_lut_name,
          keyValidation: validation.errors.sender_lut_key,
          lookupTableNameLabel: "Sender Lookup Table Name",
          customKeyField
        }
      );
    };
    this.replyToInput = () => {
      var _a, _b;
      const { config, validation } = this.props;
      return /* @__PURE__ */ React.createElement(
        Input,
        {
          id: "notification-replyto",
          name: "reply_to",
          label: "Reply-To (Optional)",
          type: "text",
          bsStyle: validation.errors.replyto ? "error" : null,
          help: ((_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.reply_to) == null ? void 0 : _b[0]) || "The email address that recipients should use for replies.",
          value: config.reply_to || "",
          onChange: this.handleChange
        }
      );
    };
    this.replyToLookupFormGroup = () => {
      var _a, _b;
      const { config, validation } = this.props;
      const customKeyField = /* @__PURE__ */ React.createElement(
        Input,
        {
          id: "reply-to-lookup-table-key",
          name: "reply_to_lut_key",
          label: "Reply To Lookup Table Key",
          type: "text",
          placeholder: LOOKUP_KEY_PLACEHOLDER_TEXT,
          bsStyle: validation.errors.reply_to_lut_key ? "error" : null,
          help: ((_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.reply_to_lut_key) == null ? void 0 : _b[0]) || "Event Field name whose value will be used as Lookup Table Key.",
          value: config.reply_to_lut_key || "",
          onChange: this.handleChange,
          required: true
        }
      );
      return /* @__PURE__ */ React.createElement(
        LookupTableFields,
        {
          onTableNameChange: (value) => this.handleSelectChange("reply_to_lut_name", value),
          onKeyChange: (value) => this.handleSelectChange("reply_to_lut_key", value),
          selectedTableName: config.reply_to_lut_name || "",
          selectedKeyName: config.reply_to_lut_key || "",
          nameValidation: validation.errors.reply_to_lut_name,
          keyValidation: validation.errors.reply_to_lut_key,
          lookupTableNameLabel: "Reply To Lookup Table Name",
          customKeyField
        }
      );
    };
  }
  render() {
    var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l;
    const { config, validation, onChange } = this.props;
    return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(EmailTemplatesRunner, { config, onChange, resetKey: (config == null ? void 0 : config.type) || (config == null ? void 0 : config.id) }), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "notification-subject",
        name: "subject",
        label: "Subject",
        type: "text",
        bsStyle: validation.errors.subject ? "error" : null,
        help: ((_b = (_a = validation == null ? void 0 : validation.errors) == null ? void 0 : _a.subject) == null ? void 0 : _b[0]) || "The subject that should be used for the email notification.",
        value: config.subject || "",
        onChange: this.handleChange,
        required: true
      }
    ), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
      Input,
      {
        type: "checkbox",
        id: "single_email",
        name: "single_email",
        label: "Send notification as a single email to all recipients.",
        onChange: this.handleChange,
        checked: config.single_email
      }
    )), config.lookup_reply_to_email ? this.replyToLookupFormGroup() : this.replyToInput(), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
      Input,
      {
        type: "checkbox",
        id: "lookup_reply_to_email",
        name: "lookup_reply_to_email",
        label: "Use lookup table for Reply To email",
        onChange: this.handleUseReplyToLookupChange,
        checked: config.lookup_reply_to_email
      }
    )), /* @__PURE__ */ React.createElement(HideOnCloud, null, config.lookup_sender_email ? this.senderLookupFormGroup() : this.senderInput(), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
      Input,
      {
        type: "checkbox",
        id: "lookup_sender_email",
        name: "lookup_sender_email",
        label: "Use lookup table for Sender email",
        onChange: this.handleUseSenderLookupChange,
        checked: config.lookup_sender_email
      }
    ))), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "users:list" }, /* @__PURE__ */ React.createElement(
      FormGroup,
      {
        controlId: "notification-user-recipients",
        validationState: validation.errors.recipients ? "error" : null
      },
      /* @__PURE__ */ React.createElement(ControlLabel, null, "User recipient(s) ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, "(Optional)")),
      /* @__PURE__ */ React.createElement(
        UsersSelectField,
        {
          value: Array.isArray(config.user_recipients) ? config.user_recipients.join(",") : "",
          onChange: this.handleRecipientsChange("user_recipients")
        }
      ),
      /* @__PURE__ */ React.createElement(HelpBlock, null, ((_d = (_c = validation == null ? void 0 : validation.errors) == null ? void 0 : _c.recipients) == null ? void 0 : _d[0]) || "Select users that will receive this Notification.")
    )), config.lookup_recipient_emails ? this.emailLookupFormGroup() : this.emailRecipientsFormGroup(), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
      Input,
      {
        type: "checkbox",
        id: "lookup_recipient_emails",
        name: "lookup_recipient_emails",
        label: "Use lookup table for Email Recipients",
        onChange: this.handleUseRecipientLookupChange,
        checked: config.lookup_recipient_emails
      }
    )), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "users:list" }, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "notification-cc-users", validationState: validation.errors.cc_users ? "error" : null }, /* @__PURE__ */ React.createElement(ControlLabel, null, "CC User(s) ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, "(Optional)")), /* @__PURE__ */ React.createElement(
      UsersSelectField,
      {
        value: Array.isArray(config.cc_users) ? config.cc_users.join(",") : "",
        onChange: this.handleRecipientsChange("cc_users")
      }
    ), /* @__PURE__ */ React.createElement(HelpBlock, null, ((_f = (_e = validation == null ? void 0 : validation.errors) == null ? void 0 : _e.cc_users) == null ? void 0 : _f[0]) || "Select users that will be on the CC line of this Notification."))), config.lookup_cc_emails ? this.ccLookupFormGroup() : this.ccEmailsFormGroup(), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
      Input,
      {
        type: "checkbox",
        id: "lookup_cc_emails",
        name: "lookup_cc_emails",
        label: "Use lookup table for CC Emails",
        onChange: this.handleUseCcLookupChange,
        checked: config.lookup_cc_emails
      }
    )), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: "users:list" }, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "notification-bcc-users", validationState: validation.errors.bcc_users ? "error" : null }, /* @__PURE__ */ React.createElement(ControlLabel, null, "BCC User(s) ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, "(Optional)")), /* @__PURE__ */ React.createElement(
      UsersSelectField,
      {
        value: Array.isArray(config.bcc_users) ? config.bcc_users.join(",") : "",
        onChange: this.handleRecipientsChange("bcc_users")
      }
    ), /* @__PURE__ */ React.createElement(HelpBlock, null, ((_h = (_g = validation == null ? void 0 : validation.errors) == null ? void 0 : _g.bcc_users) == null ? void 0 : _h[0]) || "Select users that will be on the BCC line of this Notification."))), config.lookup_bcc_emails ? this.bccLookupFormGroup() : this.bccEmailsFormGroup(), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(
      Input,
      {
        type: "checkbox",
        id: "lookup_bcc_emails",
        name: "lookup_bcc_emails",
        label: "Use lookup table for BCC Emails",
        onChange: this.handleUseBccLookupChange,
        checked: config.lookup_bcc_emails
      }
    )), /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "notification-time-zone",
        help: "Time zone used for timestamps in the email body.",
        label: /* @__PURE__ */ React.createElement(React.Fragment, null, "Time zone for date/time values ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, "(Optional)"))
      },
      /* @__PURE__ */ React.createElement(
        TimezoneSelect,
        {
          className: "timezone-select",
          name: "time_zone",
          value: config.time_zone,
          onChange: this.handleTimeZoneChange
        }
      )
    ), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "notification-body-template", validationState: validation.errors.body ? "error" : null }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Body Template"), /* @__PURE__ */ React.createElement(
      SourceCodeEditor,
      {
        id: "notification-body-template",
        mode: "text",
        theme: "light",
        value: config.body_template || "",
        onChange: this.handleBodyTemplateChange
      }
    ), /* @__PURE__ */ React.createElement(HelpBlock, null, ((_j = (_i = validation == null ? void 0 : validation.errors) == null ? void 0 : _i.body) == null ? void 0 : _j[0]) || "The template that will be used to generate the email body.")), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "notification-body-template", validationState: validation.errors.body ? "error" : null }, /* @__PURE__ */ React.createElement(ControlLabel, null, "HTML Body Template"), /* @__PURE__ */ React.createElement(
      SourceCodeEditor,
      {
        id: "notification-html-body-template",
        mode: "text",
        theme: "light",
        value: config.html_body_template || "",
        onChange: this.handleHtmlBodyTemplateChange
      }
    ), /* @__PURE__ */ React.createElement(HelpBlock, null, ((_l = (_k = validation == null ? void 0 : validation.errors) == null ? void 0 : _k.body) == null ? void 0 : _l[0]) || "The template that will be used to generate the email HTML body.")), /* @__PURE__ */ React.createElement(EventProcedureCheckbox, { checked: config.include_event_procedure, onChange: this.handleChange }));
  }
}
EmailNotificationForm.defaultConfig = {
  sender: "",
  // TODO: Default sender should come from the server. The default should be empty or the address configured in the email server settings
  // eslint-disable-next-line no-template-curly-in-string
  subject: "Event notification: ${event_definition_title}",
  // TODO: Default subject should come from the server
  reply_to: "",
  body_template: DEFAULT_BODY_TEMPLATE,
  // TODO: Default body template should come from the server
  html_body_template: DEFAULT_HTML_BODY_TEMPLATE,
  user_recipients: [],
  email_recipients: [],
  time_zone: "UTC",
  lookup_recipient_emails: false,
  recipients_lut_name: null,
  recipients_lut_key: null,
  lookup_sender_email: false,
  sender_lut_name: null,
  sender_lut_key: null,
  lookup_reply_to_email: false,
  reply_to_lut_name: null,
  reply_to_lut_key: null,
  single_email: false,
  cc_users: [],
  cc_emails: [],
  lookup_cc_emails: false,
  cc_emails_lut_name: null,
  cc_emails_lut_key: null,
  bcc_users: [],
  bcc_emails: [],
  lookup_bcc_emails: false,
  bcc_emails_lut_name: null,
  bcc_emails_lut_key: null,
  include_event_procedure: false
};
export default EmailNotificationForm;
